import React, { useState, useEffect, useCallback } from 'react';
import { Phone } from 'lucide-react';

// Image URLs from provided assets
const BANNER_IMAGE = "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/d42vkqvu_Design%20sem%20nome%20%284%29.png";

// Carousel data with lawyer information
const carouselData = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/z6a2jc88_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_57_32.png",
    name: "Débora Pessoa",
    role: "Advogada",
    specialties: "Direito de Família • Trabalhista • Civil"
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/m6e2aiam_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_55_18.png",
    name: "Guilherme Costa",
    role: "Advogado",
    specialties: "Trabalhista • Cível"
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/p1og92eh_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_58_59.png",
    name: "Bruno Pirondi",
    role: "Advogado",
    specialties: "Trabalhista"
  },
  {
    id: 4,
    image: "https://customer-assets.emergentagent.com/job_advocacia-site-3/artifacts/idmatqdz_WhatsApp%20Image%202026-02-02%20at%2016.54.29.jpeg",
    name: "Lucas Pessoa",
    role: "Advogado",
    specialties: "Trabalhista • Cível • Empresarial • Franchising • Família • Previdenciário"
  },
  {
    id: 5,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/ol4e7hvl_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_55_21.png",
    name: "Ana Carolina",
    role: "Secretária",
    specialties: "Atendimento ao Cliente"
  }
];

// Navigation items
const navItems = [
  { label: 'INÍCIO', href: '#inicio' },
  { label: 'EQUIPE', href: '#equipe' },
  { label: 'CONTEÚDOS', href: '#conteudos' },
  { label: 'QUEM SOMOS', href: '#quem-somos' },
  { label: 'CONTATO', href: '#contato' },
];

// Scroll to section function
const scrollToSection = (e, href) => {
  e.preventDefault();
  if (href === '#inicio') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const element = document.querySelector(href);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

export const HeroBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % carouselData.length);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 600);
  };

  // Auto-advance carousel every 3 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(interval);
  }, [nextSlide, isPaused]);

  const currentLawyer = carouselData[currentSlide];

  return (
    <section className="relative w-full h-screen bg-primary overflow-hidden" id="inicio">
      {/* Navigation Header */}
      <nav className="absolute top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4">
        <div className="flex items-center justify-between">
          {/* Navigation Links - Left */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="text-primary-foreground/90 font-body text-sm tracking-wider hover:text-accent transition-colors duration-300"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden text-primary-foreground p-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* CTA Button - Right */}
          <a
            href="#contato"
            onClick={(e) => scrollToSection(e, '#contato')}
            className="flex items-center gap-2 bg-accent hover:bg-accent/90 text-primary font-body font-medium text-sm px-5 py-2.5 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Agendar Consulta</span>
            <span className="sm:hidden">Contato</span>
          </a>
        </div>
      </nav>

      {/* Main Banner Content - Split Layout */}
      <div className="relative w-full h-full flex flex-col lg:flex-row">
        {/* Left Side - Banner Image (hidden on mobile, shows at bottom) */}
        <div 
          className="hidden lg:flex relative w-1/2 h-full items-center justify-center overflow-hidden"
        >
          <img
            src={BANNER_IMAGE}
            alt="Pessoa & Pessoa Advocacia"
            className="w-full h-full object-cover object-center"
          />
          {/* Smoke effect on edges */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Right edge smoke */}
            <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-primary/60 via-primary/30 to-transparent" />
            {/* Top edge smoke */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-primary/50 via-primary/20 to-transparent" />
            {/* Bottom edge smoke */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary/70 via-primary/30 to-transparent" />
          </div>
        </div>

        {/* Center Smoke/Fog Divider */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-80 -translate-x-1/2 z-20 pointer-events-none">
          {/* Core solid center */}
          <div className="absolute inset-y-0 left-[35%] right-[35%] bg-primary" />
          {/* Gradient fade left */}
          <div className="absolute inset-y-0 left-0 w-[40%] bg-gradient-to-r from-transparent to-primary" />
          {/* Gradient fade right */}
          <div className="absolute inset-y-0 right-0 w-[40%] bg-gradient-to-l from-transparent to-primary" />
          {/* Soft blur overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/80 to-transparent blur-xl" />
        </div>

        {/* Right Side - Carousel (full width on mobile) */}
        <div 
          className="relative w-full lg:w-1/2 h-full overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Carousel Images */}
          {carouselData.map((lawyer, index) => (
            <div
              key={lawyer.id}
              className={`absolute inset-0 transition-all duration-700 ease-out ${
                index === currentSlide 
                  ? 'opacity-100 scale-100' 
                  : 'opacity-0 scale-105'
              }`}
            >
              <img
                src={lawyer.image}
                alt={lawyer.name}
                className="w-full h-full object-cover object-center"
              />
              {/* Dark overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/20 to-transparent" />
            </div>
          ))}

          {/* Smoke effect on carousel edges */}
          <div className="absolute inset-0 pointer-events-none z-10">
            {/* Left edge smoke */}
            <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-primary/60 via-primary/30 to-transparent" />
            {/* Top edge smoke */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-primary/50 via-primary/20 to-transparent" />
            {/* Right edge smoke */}
            <div className="absolute top-0 right-0 w-24 h-full bg-gradient-to-l from-primary/40 via-primary/20 to-transparent" />
          </div>

          {/* Lawyer Info on Carousel Side */}
          <div className="absolute bottom-28 lg:bottom-40 left-4 right-4 lg:left-auto lg:right-12 z-20 text-center lg:text-right">
            <div key={currentSlide} className="animate-fade-in">
              <p className="text-accent font-body text-sm lg:text-base font-medium mb-1 tracking-wide">
                {currentLawyer.role}
              </p>
              <h3 className="font-display text-2xl lg:text-4xl text-primary-foreground font-semibold mb-2">
                {currentLawyer.name}
              </h3>
              <p className="text-primary-foreground/70 font-body text-xs lg:text-sm max-w-xs mx-auto lg:mx-0 lg:ml-auto">
                {currentLawyer.specialties}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Center Overlay - Firm Name & Tagline */}
      <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none">
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/80 to-transparent h-32 lg:h-48" />
        
        <div className="relative text-center pb-4 lg:pb-12 px-4">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl xl:text-7xl text-primary-foreground font-semibold tracking-tight mb-2 lg:mb-3">
            Pessoa & Pessoa
          </h1>
          <p className="text-primary-foreground/80 font-body text-xs sm:text-sm lg:text-lg italic max-w-2xl mx-auto leading-relaxed">
            "Excelência jurídica e compromisso com seus direitos."
            <span className="hidden lg:inline">
              <br />
              Defendendo seus interesses com dedicação e profissionalismo.
            </span>
          </p>
          
          {/* Gold accent line */}
          <div className="mt-2 lg:mt-4 mx-auto w-16 lg:w-24 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent" />
        </div>
      </div>

      {/* Bottom smoke transition to next section */}
      <div className="absolute -bottom-2 left-0 right-0 h-32 pointer-events-none z-40">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-primary" />
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5511995579395?text=Olá! Gostaria de agendar uma consulta com a equipe Pessoa %26 Pessoa."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-body font-medium text-sm px-4 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span>Agendar Consulta</span>
      </a>
    </section>
  );
};

export default HeroBanner;
